"use client"

import { useState, useContext, useEffect, useMemo } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Chip,
  alpha,
  Tooltip,
  Badge,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { LocationOn, Add, DirectionsBus, Public, Explore, CalendarToday, ExpandMore, RoomService, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material"

const GestionDestinosViajes = () => {
  const { ubicaciones, getUbicacion, postUbicacion, isNavbarCollapsed, getActividadesByDestino, updateUbicacion, deleteUbicacion } = useContext(ErpContext)
  
  const [descripcionDestino, setDescripcionDestino] = useState("")
  const [actividadesPorDestino, setActividadesPorDestino] = useState({})
  const [fechasViajePorDestino, setFechasViajePorDestino] = useState({}) // 🔧 NUEVO: Estado para fechas de viaje
  const [modalActividades, setModalActividades] = useState({
    open: false,
    destino: null,
    actividades: []
  })
  const [modalFechasViaje, setModalFechasViaje] = useState({ // 🔧 NUEVO: Modal para fechas de viaje
    open: false,
    destino: null,
    fechas: []
  })
  const [modalEditarDestino, setModalEditarDestino] = useState({
    open: false,
    destino: null,
    descripcion: ""
  })
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    destino: null
  })
  const [filtroDescripcion, setFiltroDescripcion] = useState("")
  const [filtroActividad, setFiltroActividad] = useState("")
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("")
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("")
  const [sortModel, setSortModel] = useState([{ field: "createdAt", sort: "desc" }])
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    severity: "success"
  })

  const formatFechaCreacion = (fechaIso) => {
    if (!fechaIso) return "-"
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const userLocale = navigator?.language || "es-AR"

    try {
      return new Intl.DateTimeFormat(userLocale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: userTimeZone
      }).format(new Date(fechaIso))
    } catch (error) {
      console.error("Error formateando fecha de creación:", error)
      return new Date(fechaIso).toLocaleDateString("es-AR")
    }
  }

  const sortedUbicaciones = useMemo(() => {
    const normalizarTexto = (txt) => (txt || "").toString().toLowerCase()
    const filtroDesc = normalizarTexto(filtroDescripcion)
    const filtroAct = normalizarTexto(filtroActividad)

    const fechaDesdeMs = filtroFechaDesde ? new Date(`${filtroFechaDesde}T00:00:00`).getTime() : null
    const fechaHastaMs = filtroFechaHasta ? new Date(`${filtroFechaHasta}T23:59:59`).getTime() : null

    const lista = Array.isArray(ubicaciones)
      ? ubicaciones
          .filter((u) => u && u.eliminado === false)
          .filter((u) => u.esDestino === true)
          .filter((u) => {
            if (!filtroDesc) return true
            return normalizarTexto(u.descripcion).includes(filtroDesc)
          })
          .filter((u) => {
            if (!fechaDesdeMs && !fechaHastaMs) return true
            const createdMs = u.createdAt ? new Date(u.createdAt).getTime() : null
            if (!createdMs) return false
            if (fechaDesdeMs && createdMs < fechaDesdeMs) return false
            if (fechaHastaMs && createdMs > fechaHastaMs) return false
            return true
          })
          .filter((u) => {
            if (!filtroAct) return true
            const actividades = actividadesPorDestino[u.id] || []
            return actividades.some((act) => normalizarTexto(act.descripcion).includes(filtroAct) || normalizarTexto(act.codigo).includes(filtroAct))
          })
          .map((u) => ({ ...u }))
      : []

    return lista.sort((a, b) => {
      const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0
      const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0
      return dateB - dateA || (b?.id ?? 0) - (a?.id ?? 0)
    })
  }, [ubicaciones, filtroDescripcion, filtroActividad, filtroFechaDesde, filtroFechaHasta, actividadesPorDestino])

  useEffect(() => {
    getUbicacion()
  }, [])

  // Cargar actividades para cada destino cuando se cargan las ubicaciones
  useEffect(() => {
    if (ubicaciones && ubicaciones.length > 0) {
      cargarActividadesPorDestino()
    }
  }, [ubicaciones])

  const cargarActividadesPorDestino = async () => {
    const actividadesMap = {}
    const fechasViajeMap = {} // 🔧 NUEVO: Mapa para fechas de viaje
    
    for (const destino of ubicaciones) {
      try {
        const response = await getActividadesByDestino(destino.id)
        actividadesMap[destino.id] = response.actividades || []
        fechasViajeMap[destino.id] = response.fechasViaje || [] // 🔧 NUEVO: Guardar fechas de viaje
      } catch (error) {
        console.error(`Error al cargar actividades para destino ${destino.id}:`, error)
        actividadesMap[destino.id] = []
        fechasViajeMap[destino.id] = [] // 🔧 NUEVO: Inicializar array vacío en caso de error
      }
    }
    
    setActividadesPorDestino(actividadesMap)
    setFechasViajePorDestino(fechasViajeMap) // 🔧 NUEVO: Establecer fechas de viaje
  }

  const handleDestinoSubmit = async (e) => {
    e.preventDefault()
    
    if (!descripcionDestino.trim()) {
      setAlertState({
        open: true,
        message: "Por favor ingrese un nombre de destino",
        severity: "warning"
      })
      return
    }

    try {
      await postUbicacion(descripcionDestino, true)
      setDescripcionDestino("")
      setAlertState({
        open: true,
        message: "Destino agregado exitosamente",
        severity: "success"
      })
      getUbicacion()
    } catch (error) {
      setAlertState({
        open: true,
        message: "Error al agregar destino",
        severity: "error"
      })
    }
  }

  const handleCloseAlert = () => {
    setAlertState({ ...alertState, open: false })
  }

  const handleSortModelChange = (model) => {
    const clickedField = Array.isArray(model) && model.length > 0 ? model[0]?.field : null
    if (clickedField === "actividades" || clickedField === "fechasViaje") {
      return
    }
    if (!model || model.length === 0) {
      setSortModel([{ field: "createdAt", sort: "desc" }])
      return
    }
    setSortModel([model[0]])
  }

  const handleOpenModalActividades = (destino) => {
    const actividades = actividadesPorDestino[destino.id] || []
    setModalActividades({
      open: true,
      destino: destino,
      actividades: actividades
    })
  }

  const handleCloseModalActividades = () => {
    setModalActividades({
      open: false,
      destino: null,
      actividades: []
    })
  }

  // 🔧 NUEVO: Funciones para manejar modal de fechas de viaje
  const handleOpenModalFechasViaje = (destino) => {
    const fechas = fechasViajePorDestino[destino.id] || []
    setModalFechasViaje({
      open: true,
      destino: destino,
      fechas: fechas
    })
  }

  const handleCloseModalFechasViaje = () => {
    setModalFechasViaje({
      open: false,
      destino: null,
      fechas: []
    })
  }

  const handleOpenEditarDestino = (destino) => {
    setModalEditarDestino({
      open: true,
      destino,
      descripcion: destino?.descripcion || ""
    })
  }

  const handleCloseEditarDestino = () => {
    setModalEditarDestino({
      open: false,
      destino: null,
      descripcion: ""
    })
  }

  const handleGuardarEdicion = async () => {
    if (!modalEditarDestino.descripcion.trim() || !modalEditarDestino.destino?.id) return
    try {
      await updateUbicacion(modalEditarDestino.destino.id, modalEditarDestino.descripcion.trim())
      setAlertState({
        open: true,
        message: "Destino actualizado correctamente",
        severity: "success"
      })
      handleCloseEditarDestino()
    } catch (error) {
      setAlertState({
        open: true,
        message: "Error al actualizar el destino",
        severity: "error"
      })
    }
  }

  const handleOpenConfirmDelete = (destino) => {
    setConfirmDelete({ open: true, destino })
  }

  const handleCloseConfirmDelete = () => {
    setConfirmDelete({ open: false, destino: null })
  }

  const handleEliminarDestino = async () => {
    if (!confirmDelete.destino?.id) return
    try {
      await deleteUbicacion(confirmDelete.destino.id)
      setAlertState({
        open: true,
        message: "Destino eliminado",
        severity: "info"
      })
    } catch (error) {
      const backendMessage = error?.response?.data?.message
      const isConflict = error?.response?.status === 409
      setAlertState({
        open: true,
        message: backendMessage || "Error al eliminar destino",
        severity: isConflict ? "warning" : "error"
      })
    } finally {
      handleCloseConfirmDelete()
    }
  }

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.2,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          ID
        </Typography>
      ),
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined" 
          sx={{ fontWeight: 600 }} 
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Fecha",
      flex: 0.3,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      type: "number",
      sortable: true,
      valueGetter: (params) => {
        // Compatible con MUI DataGrid v6+ (params es el valor) y v5 (params es objeto con row)
        const created = typeof params === 'string' ? params : params?.row?.createdAt
        if (!created) return 0
        const ts = new Date(created).getTime()
        return isNaN(ts) ? 0 : ts
      },
      sortComparator: (v1, v2) => v1 - v2,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Fecha
        </Typography>
      ),
      renderCell: (params) => {
        const fecha = formatFechaCreacion(params.row.createdAt)
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CalendarToday fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {fecha}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "descripcion",
      headerName: "Destino",
      flex: 1,
      minWidth: 300,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Destino
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DirectionsBus fontSize="small" sx={{ color: "rgb(244, 177, 14)" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actividades",
      headerName: "Actividades",
      flex: 0.4,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      headerClassName: "no-sort-indicator-header",
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Actividades Asociadas
        </Typography>
      ),
      renderCell: (params) => {
        const actividades = actividadesPorDestino[params.row.id] || []
        const cantidadActividades = actividades.length
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Tooltip 
              title={
                cantidadActividades > 0 
                  ? `Click para ver ${cantidadActividades} actividad${cantidadActividades > 1 ? 'es' : ''}`
                  : "No hay actividades asociadas"
              }
            >
              <Box
                onClick={() => cantidadActividades > 0 && handleOpenModalActividades(params.row)}
                sx={{
                  cursor: cantidadActividades > 0 ? "pointer" : "default",
                  "&:hover": cantidadActividades > 0 ? {
                    transform: "scale(1.05)",
                    transition: "transform 0.2s ease"
                  } : {}
                }}
              >
                <Badge 
                  badgeContent={cantidadActividades} 
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: cantidadActividades > 0 ? "rgb(244, 177, 14)" : "#ccc",
                      color: "white",
                      fontWeight: "bold"
                    }
                  }}
                >
                  <Explore 
                    sx={{ 
                      color: cantidadActividades > 0 ? "rgb(244, 177, 14)" : "#ccc",
                      fontSize: 20 
                    }} 
                  />
                </Badge>
              </Box>
            </Tooltip>
          </Box>
        )
      },
    },
    // 🔧 NUEVA COLUMNA: Fechas de Viaje
    {
      field: "fechasViaje",
      headerName: "Fechas de Viaje",
      flex: 0.5,
      minWidth: 250,
      headerAlign: "center",
      align: "center",
      headerClassName: "no-sort-indicator-header",
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Próximas Fechas
        </Typography>
      ),
      renderCell: (params) => {
        const fechas = fechasViajePorDestino[params.row.id] || []
        const cantidadFechas = fechas.length
        
        if (cantidadFechas === 0) {
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                Sin fechas
              </Typography>
            </Box>
          )
        }

         // Mostrar las primeras 2 fechas
         const primerasFechas = fechas.slice(0, 2)
         const hayMasFechas = cantidadFechas > 2

         return (
           <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, width: "100%" }}>
             {/* Fechas a la izquierda */}
             <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.5  }}>
               {primerasFechas.map((fecha, index) => (
                 <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                   <CalendarToday sx={{ fontSize: 14, color: "rgb(244, 177, 14)" }} />
                   <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                     {new Date(fecha.fechaInicio).toLocaleDateString("es-AR", {
                       day: "2-digit",
                       month: "2-digit"
                     })}
                     {fecha.fechaFin && (
                       <>
                         {" - "}
                         {new Date(fecha.fechaFin).toLocaleDateString("es-AR", {
                           day: "2-digit",
                           month: "2-digit"
                         })}
                       </>
                     )}
                   </Typography>
                 </Box>
               ))}
             </Box>
             
             {/* Botón "Ver más" SIEMPRE visible */}
             <Tooltip title={hayMasFechas ? `Ver todas las ${cantidadFechas} fechas y servicios` : `Ver servicios del paquete`}>
               <Box
                 onClick={() => handleOpenModalFechasViaje(params.row)}
                 sx={{
                   cursor: "pointer",
                   display: "flex",
                   alignItems: "center",
                   gap: 0.5,
                   px: 1.5,
                   py: 0.5,
                   borderRadius: 1,
                   backgroundColor: alpha("rgb(244, 177, 14)", 0.1),
                   border: `1px solid ${alpha("rgb(244, 177, 14)", 0.3)}`,
                   whiteSpace: "nowrap",
                   "&:hover": {
                     backgroundColor: alpha("rgb(244, 177, 14)", 0.2),
                     transform: "scale(1.05)",
                     transition: "all 0.2s ease"
                   }
                 }}
               >
                 <CalendarToday sx={{ fontSize: 12, color: "rgb(244, 177, 14)" }} />
                 <Typography variant="body2" sx={{ fontSize: "0.7rem", fontWeight: 600, color: "rgb(244, 177, 14)" }}>
                   Ver más
                 </Typography>
               </Box>
             </Tooltip>
           </Box>
         )
      },
    },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 0.3,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Acciones
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <Tooltip title="Editar destino">
            <IconButton size="small" color="primary" onClick={() => handleOpenEditarDestino(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar destino">
            <IconButton size="small" color="error" onClick={() => handleOpenConfirmDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "xl",
        mx: "auto",
        px: 3,
        py: 1.5,
        transition: "margin-left 0.3s ease",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              backgroundColor: "rgba(244, 177, 14, 0.1)",
              borderRadius: "50%",
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Public sx={{ color: "rgb(244, 177, 14)", fontSize: 24 }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#5b5b5f",
            }}
          >
            Gestión de Destinos
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "#666", ml: 5.5, fontSize: "0.85rem" }}>
          Administra los destinos de viaje disponibles en el sistema
        </Typography>
      </Box>

      {/* Formulario para agregar destino */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
      <form onSubmit={handleDestinoSubmit}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                backgroundColor: "rgba(244, 177, 14, 0.1)",
                borderRadius: "50%",
                p: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <LocationOn sx={{ color: "rgb(244, 177, 14)", fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#5b5b5f", whiteSpace: "nowrap", fontSize: "0.95rem" }}>
              Agregar Nuevo Destino
            </Typography>
          </Box>

          <TextField
            label="Destino"
            variant="outlined"
            value={descripcionDestino}
            onChange={(e) => setDescripcionDestino(e.target.value)}
            placeholder="Ej: Rosario, Temaiken, Bariloche..."
            size="small"
            sx={{
              minWidth: 200,
              width: { xs: "100%", sm: 240, md: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              }
            }}
          />

          <Button
            variant="contained"
            type="submit"
            startIcon={<Add />}
            sx={{
              backgroundColor: "#5b5b5f",
              "&:hover": { backgroundColor: "#4a4a4e" },
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              py: 0.9,
              borderRadius: 2,
              whiteSpace: "nowrap",
            }}
          >
            Agregar
          </Button>
        </Box>
      </form>
        </CardContent>
      </Card>

      {/* Filtros y estadísticas */}
      <Box sx={{ mb: 1.5, flexShrink: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", flex: 1 }}>
              <TextField
                label="Filtrar por destino"
                size="small"
                value={filtroDescripcion}
                onChange={(e) => setFiltroDescripcion(e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Filtrar por actividad"
                size="small"
                value={filtroActividad}
                onChange={(e) => setFiltroActividad(e.target.value)}
                sx={{ minWidth: 200 }}
                placeholder="Código o descripción"
              />
              <TextField
                label="Fecha desde"
                type="date"
                size="small"
                value={filtroFechaDesde}
                onChange={(e) => setFiltroFechaDesde(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Fecha hasta"
                type="date"
                size="small"
                value={filtroFechaHasta}
                onChange={(e) => setFiltroFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="text"
                onClick={() => {
                  setFiltroDescripcion("")
                  setFiltroActividad("")
                  setFiltroFechaDesde("")
                  setFiltroFechaHasta("")
                }}
                sx={{ whiteSpace: "nowrap", textTransform: "none", fontWeight: 600 }}
              >
                Limpiar filtros
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
              <Chip
                icon={<DirectionsBus />}
                label={`Destinos: ${sortedUbicaciones.length}`}
                color="primary"
                variant="filled"
                sx={{
                  backgroundColor: "rgb(244, 177, 14)",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  py: 1.5,
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* DataGrid con destinos */}
      <Paper 
        elevation={0} 
        sx={{ 
          flex: 1,
          minHeight: 0,
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <DataGrid
          key={isNavbarCollapsed ? "collapsed" : "expanded"}
          rows={sortedUbicaciones}
          rowHeight={40}   
          columns={columns}
          getRowId={(row) => row.id}
          headerHeight={40}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingOrder={["desc", "asc"]}
          disableSelectionOnClick
          components={{
            Toolbar: GridToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            width: "100%",
            border: 0,
            flex: 1,
            minHeight: 0,
            "& .MuiDataGrid-root": {
              height: "100%",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#5b5b5f",
              color: "white",
              borderBottom: "2px solid #333",
              height: "50px !important",
              minHeight: "50px !important",
              maxHeight: "50px !important",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "50px !important",
              minHeight: "50px !important",
              maxHeight: "50px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              lineHeight: "50px !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiDataGrid-columnHeader[data-field='actividades'] .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader[data-field='actividades'] .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader[data-field='actividades'] .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader[data-field='fechasViaje'] .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader[data-field='fechasViaje'] .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader[data-field='fechasViaje'] .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .no-sort-indicator-header .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .no-sort-indicator-header .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .no-sort-indicator-header .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiInputBase-root": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiTablePagination-root": {
              color: "black !important",
            },
            "& .MuiSelect-icon": {
              color: "rgb(244, 177, 14) !important",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row": {
              "&:nth-of-type(even)": {
                backgroundColor: "#fafafa",
              },
              "&:hover": {
                backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
                transition: "background 0.3s ease",
              },
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "2px solid #e0e0e0",
              backgroundColor: "#fafafa",
            },
          }}
        />
      </Paper>

      {/* Modal para mostrar actividades detalladas */}
      <Dialog
        open={modalActividades.open}
        onClose={handleCloseModalActividades}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "rgb(244, 177, 14)", 
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <Explore />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Actividades en {modalActividades.destino?.descripcion}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {modalActividades.actividades.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Explore sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No hay actividades asociadas a este destino
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Las actividades aparecerán aquí cuando se creen servicios marcados como "Es Actividad" 
                y se asocien a este destino.
              </Typography>
            </Box>
          ) : (
            <List sx={{ width: "100%" }}>
              {modalActividades.actividades.map((actividad, index) => (
                <Box key={actividad.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Explore sx={{ color: "rgb(244, 177, 14)" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {actividad.codigo} - {actividad.descripcion}
                          </Typography>
                          {actividad.descripcionServicio && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {actividad.descripcionServicio}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < modalActividades.actividades.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: "#fafafa" }}>
          <Button 
            onClick={handleCloseModalActividades}
            variant="contained"
            sx={{
              backgroundColor: "#5b5b5f",
              "&:hover": { backgroundColor: "#4a4a4e" },
              textTransform: "none",
              fontWeight: 600
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🔧 NUEVO: Modal para mostrar fechas de viaje detalladas */}
      <Dialog
        open={modalFechasViaje.open}
        onClose={handleCloseModalFechasViaje}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "rgb(244, 177, 14)", 
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1
        }}>
          <CalendarToday />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Fechas de Viaje - {modalFechasViaje.destino?.descripcion}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {modalFechasViaje.fechas.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CalendarToday sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No hay fechas de viaje programadas para este destino
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Las fechas aparecerán aquí cuando se creen paquetes de viaje 
                asociados a este destino.
              </Typography>
            </Box>
           ) : (
             <Box sx={{ width: "100%" }}>
               {modalFechasViaje.fechas.map((fecha, index) => (
                 <Accordion 
                   key={index} 
                   sx={{ 
                     mb: 2, 
                     boxShadow: 1,
                     "&:before": { display: "none" },
                     borderRadius: 2,
                     border: "1px solid #e0e0e0"
                   }}
                 >
                   <AccordionSummary
                     expandIcon={<ExpandMore sx={{ color: "rgb(244, 177, 14)" }} />}
                     sx={{
                       backgroundColor: alpha("rgb(244, 177, 14)", 0.05),
                       borderRadius: "8px 8px 0 0",
                       "& .MuiAccordionSummary-content": {
                         alignItems: "center",
                         gap: 2
                       }
                     }}
                   >
                     <CalendarToday sx={{ color: "rgb(244, 177, 14)", mr: 1 }} />
                     <Box sx={{ flex: 1 }}>
                       <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                         {fecha.paquete.codigo} - {fecha.paquete.descripcion}
                       </Typography>
                       <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                         <Chip 
                           label={`Inicio: ${new Date(fecha.fechaInicio).toLocaleDateString("es-AR", {
                             day: "2-digit",
                             month: "2-digit",
                             year: "numeric"
                           })}`} 
                           size="small" 
                           variant="outlined"
                           sx={{ fontSize: "0.75rem" }}
                         />
                         {fecha.fechaFin && (
                           <Chip 
                             label={`Fin: ${new Date(fecha.fechaFin).toLocaleDateString("es-AR", {
                               day: "2-digit",
                               month: "2-digit",
                               year: "numeric"
                             })}`} 
                             size="small" 
                             variant="outlined"
                             sx={{ fontSize: "0.75rem" }}
                           />
                         )}
                         <Chip 
                           label={`${fecha.servicios?.length || 0} servicios`} 
                           size="small" 
                           color="primary"
                           sx={{ fontSize: "0.75rem" }}
                         />
                       </Box>
                     </Box>
                   </AccordionSummary>
                   
                   <AccordionDetails sx={{ p: 0 }}>
                     {fecha.servicios && fecha.servicios.length > 0 ? (
                       <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
                         <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "#5b5b5f" }}>
                           Servicios incluidos en este paquete:
                         </Typography>
                         <List sx={{ width: "100%", py: 0 }}>
                           {fecha.servicios.map((servicio, servicioIndex) => (
                             <Box key={servicio.id}>
                               <ListItem sx={{ px: 0, py: 1 }}>
                                 <ListItemIcon>
                                   <RoomService sx={{ color: "rgb(244, 177, 14)", fontSize: 20 }} />
                                 </ListItemIcon>
                                 <ListItemText
                                   primary={
                                     <Box>
                                       <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                         {servicio.codigo} - {servicio.descripcion}
                                       </Typography>
                                       {servicio.descripcionServicio && (
                                         <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.8rem" }}>
                                           {servicio.descripcionServicio}
                                         </Typography>
                                       )}
                                     </Box>
                                   }
                                 />
                               </ListItem>
                               {servicioIndex < fecha.servicios.length - 1 && <Divider />}
                             </Box>
                           ))}
                         </List>
                       </Box>
                     ) : (
                       <Box sx={{ p: 2, textAlign: "center", backgroundColor: "#fafafa" }}>
                         <RoomService sx={{ fontSize: 32, color: "#ccc", mb: 1 }} />
                         <Typography variant="body2" color="text.secondary">
                           No hay servicios asociados a este paquete
                         </Typography>
                       </Box>
                     )}
                   </AccordionDetails>
                 </Accordion>
               ))}
             </Box>
           )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: "#fafafa" }}>
          <Button 
            onClick={handleCloseModalFechasViaje}
            variant="contained"
            sx={{
              backgroundColor: "#5b5b5f",
              "&:hover": { backgroundColor: "#4a4a4e" },
              textTransform: "none",
              fontWeight: 600
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal editar destino */}
      <Dialog
        open={modalEditarDestino.open}
        onClose={handleCloseEditarDestino}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar destino</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Destino"
            value={modalEditarDestino.descripcion}
            onChange={(e) => setModalEditarDestino((prev) => ({ ...prev, descripcion: e.target.value }))}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditarDestino}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardarEdicion}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación eliminar destino */}
      <Dialog
        open={confirmDelete.open}
        onClose={handleCloseConfirmDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar destino</DialogTitle>
        <DialogContent dividers>
          <Typography>
            ¿Seguro que deseas eliminar el destino "{confirmDelete.destino?.descripcion}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDelete}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleEliminarDestino}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para alertas */}
      <Snackbar
        open={alertState.open}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alertState.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default GestionDestinosViajes
